/**
 * Theme Context - Global dark mode management
 * Persists user preference to localStorage, applies 'dark' class on <html>
 * Defaults to light mode when no user preference is stored
 */

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

type Theme = 'light' | 'dark';

interface ThemeContextValue {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (t: Theme) => void;
  isDark: boolean;
  /** Whether theme is following system preference (no user override) */
  isSystemPreference: boolean;
  /** Reset to system preference */
  resetToSystem: () => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: 'light',
  toggleTheme: () => {},
  setTheme: () => {},
  isDark: false,
  isSystemPreference: true,
  resetToSystem: () => {},
});

const STORAGE_KEY = 'scrudix_theme';

/** Detect system color scheme preference */
function getSystemTheme(): Theme {
  if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isSystemPreference, setIsSystemPreference] = useState<boolean>(false);

  const [theme, setThemeState] = useState<Theme>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === 'dark' || stored === 'light') return stored;
    } catch {}
    // First-visit default: white/light theme
    return 'light';
  });

  // Listen for system theme changes only if user explicitly reset to system
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      if (isSystemPreference) {
        setThemeState(e.matches ? 'dark' : 'light');
      }
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [isSystemPreference]);

  // Sync <html> class and localStorage whenever theme changes
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    // Only persist if user explicitly chose (not system auto)
    if (!isSystemPreference) {
      try { localStorage.setItem(STORAGE_KEY, theme); } catch {}
    }
  }, [theme, isSystemPreference]);

  const enableThemeTransition = useCallback(() => {
    const root = document.documentElement;
    root.classList.add('theme-transition');
    window.setTimeout(() => {
      root.classList.remove('theme-transition');
    }, 500);
  }, []);

  const setTheme = useCallback((t: Theme) => {
    enableThemeTransition();
    setIsSystemPreference(false);
    setThemeState(t);
    try { localStorage.setItem(STORAGE_KEY, t); } catch {}
  }, [enableThemeTransition]);

  const toggleTheme = useCallback(() => {
    enableThemeTransition();
    setIsSystemPreference(false);
    setThemeState((prev) => {
      const next = prev === 'dark' ? 'light' : 'dark';
      try { localStorage.setItem(STORAGE_KEY, next); } catch {}
      return next;
    });
  }, [enableThemeTransition]);

  const resetToSystem = useCallback(() => {
    setIsSystemPreference(true);
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
    setThemeState(getSystemTheme());
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme, isDark: theme === 'dark', isSystemPreference, resetToSystem }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);

export default ThemeContext;
